import { escapeHtml, formatCount, josa } from '../format';
import type { AnalysisSetup, ParseSummary } from '../types';
import {
  canUndo,
  createCleanupState,
  displayName,
  isGroupRoom,
  mergeRows,
  MAX_TITLE_LENGTH,
  renameRow,
  setMe,
  setTitle,
  toAnalysisSetup,
  undoMerge,
  validateCleanup,
  type CleanupRow,
  type CleanupState,
} from './cleanup-model';
import { warningMessage } from './copy';
import { requireElement, type MountedScreen } from './dom';
import { formatCompactPeriod, formatPeriod } from './format-ui';
import { icon } from './icons';

/**
 * Name cleanup (spec §6.2-3): rename (20 chars), choose 나 (optional, the
 * exporter label is preselected), merge two or more rows with undo, and a room
 * name for groups. "성적표 보기" stays disabled until at least two people
 * with distinct, valid names remain.
 */

export interface CleanupScreenOptions {
  readonly summary: ParseSummary;
  /** Restores an earlier cleanup (e.g. "이름 다시 정리하기" from the result). */
  readonly initialState?: CleanupState;
  readonly onSubmit: (setup: AnalysisSetup, state: CleanupState) => void;
  readonly onRestart: () => void;
}

/** Radio chips up to this many people; a select above it. */
const MAX_ME_CHIPS = 8;

function rowMarkup(row: CleanupRow, checked: boolean): string {
  const key = escapeHtml(row.key);
  const name = displayName(row);
  const origin = row.rawNames.length > 1 ? `합친 이름: ${row.rawNames.join(', ')}` : `원래 이름: ${row.rawNames[0] ?? ''}`;
  return `
    <li class="krc-person" data-key="${key}">
      <label class="krc-check">
        <input type="checkbox" data-merge-check value="${key}"${checked ? ' checked' : ''} aria-describedby="krc-meta-${key}" />
        <span class="krc-check-box" aria-hidden="true">${icon('check')}</span>
        <span class="krc-sr-only" data-check-label>${escapeHtml(name)} 합치기 선택</span>
      </label>
      <div class="krc-person-body">
        <label class="krc-person-label" for="krc-name-${key}">이름 <span class="krc-person-origin">${escapeHtml(origin)}</span></label>
        <input id="krc-name-${key}" class="krc-input" type="text" value="${escapeHtml(row.name)}" placeholder="${escapeHtml(row.defaultName)}" maxlength="40" autocomplete="off" spellcheck="false" data-name-input="${key}" aria-describedby="krc-meta-${key} krc-name-error-${key}" />
        <p class="krc-person-meta" id="krc-meta-${key}">메시지 ${formatCount(row.messageCount)}개 · ${escapeHtml(formatCompactPeriod(row.firstAt, row.lastAt))}</p>
        <p class="krc-field-error" id="krc-name-error-${key}" data-name-error="${key}"></p>
      </div>
    </li>`;
}

function meOptionsMarkup(state: CleanupState): string {
  if (state.rows.length <= MAX_ME_CHIPS) {
    const chips = state.rows.map((row) => `
      <label class="krc-chip-radio"><input type="radio" name="krc-me" value="${escapeHtml(row.key)}"${state.meKey === row.key ? ' checked' : ''} /><span data-me-label="${escapeHtml(row.key)}">${escapeHtml(displayName(row))}</span></label>`).join('');
    return `${chips}<label class="krc-chip-radio is-skip"><input type="radio" name="krc-me" value=""${state.meKey === null ? ' checked' : ''} /><span>건너뛰기</span></label>`;
  }
  const options = state.rows.map((row) => `<option value="${escapeHtml(row.key)}"${state.meKey === row.key ? ' selected' : ''} data-me-label="${escapeHtml(row.key)}">${escapeHtml(displayName(row))}</option>`).join('');
  return `<select class="krc-input krc-me-select" id="krc-me-select" aria-labelledby="krc-me-legend"><option value=""${state.meKey === null ? ' selected' : ''}>건너뛰기</option>${options}</select>`;
}

export function mountCleanupScreen(stage: HTMLElement, options: CleanupScreenOptions): MountedScreen {
  const { summary } = options;
  let state: CleanupState = options.initialState ?? createCleanupState(summary);
  const checked = new Set<string>();

  const warnings = summary.warnings.filter((warning) => warning.code !== 'EXPORTER_LABEL_DETECTED' || state.meKey !== null);
  const section = document.createElement('section');
  section.className = 'krc-screen krc-cleanup';
  section.setAttribute('aria-labelledby', 'krc-cleanup-title');
  section.innerHTML = `
    <div class="krc-card krc-cleanup-card">
      <h2 class="krc-screen-title" id="krc-cleanup-title" tabindex="-1">누가 누구인지 확인해 주세요</h2>
      <p class="krc-lead">이름은 성적표와 저장 이미지에 그대로 들어가요. 별명으로 바꿔도 좋아요.</p>
      <dl class="krc-summary-stats">
        <div><dt>메시지</dt><dd>${formatCount(summary.messageCount)}개</dd></div>
        <div><dt>기간</dt><dd>${escapeHtml(formatPeriod(summary.firstAt, summary.lastAt))}</dd></div>
        <div><dt>참여자</dt><dd>${formatCount(summary.participants.length)}명</dd></div>
      </dl>
      ${warnings.length ? `<ul class="krc-warnings">${warnings.map((warning) => `<li>${icon('alert')}<span>${escapeHtml(warningMessage(warning))}</span></li>`).join('')}</ul>` : ''}
      <form class="krc-cleanup-form" novalidate>
        <fieldset class="krc-fieldset">
          <legend>이름 정리</legend>
          <p class="krc-field-help">같은 사람이 이름 두 개로 나뉘어 있으면 둘 다 체크한 뒤 합쳐 주세요.</p>
          <ul class="krc-people" data-people></ul>
          <div class="krc-merge-bar">
            <button type="button" class="button button-soft krc-merge-button" data-merge disabled>선택한 사람 합치기</button>
            <button type="button" class="krc-text-button" data-undo hidden>${icon('refresh')}<span>합치기 되돌리기</span></button>
          </div>
        </fieldset>
        <fieldset class="krc-fieldset krc-me-field">
          <legend id="krc-me-legend">이 중에 나는?</legend>
          <p class="krc-field-help">고르면 성적표에 ‘나’ 표시가 붙어요. 건너뛰어도 괜찮아요.</p>
          <div class="krc-me-options" data-me-options></div>
        </fieldset>
        <div class="krc-title-field" data-title-field hidden>
          <label for="krc-room-title">방 이름 <small>선택 · ${MAX_TITLE_LENGTH}자까지</small></label>
          <input id="krc-room-title" class="krc-input" type="text" maxlength="${MAX_TITLE_LENGTH * 2}" autocomplete="off" placeholder="예: 우리 동기방" />
        </div>
        <p class="krc-form-error" data-form-error role="alert"></p>
        <button type="submit" class="button button-primary krc-submit" data-submit>${icon('sparkle')}<span>성적표 보기</span></button>
      </form>
      <button type="button" class="krc-text-button krc-restart-link" data-restart>다른 파일 고르기</button>
      <p class="krc-sr-only" aria-live="polite" data-cleanup-live></p>
    </div>`;
  stage.append(section);

  const form = requireElement<HTMLFormElement>(section, 'form');
  const peopleList = requireElement<HTMLUListElement>(section, '[data-people]');
  const meOptions = requireElement<HTMLElement>(section, '[data-me-options]');
  const mergeButton = requireElement<HTMLButtonElement>(section, '[data-merge]');
  const undoButton = requireElement<HTMLButtonElement>(section, '[data-undo]');
  const titleField = requireElement<HTMLElement>(section, '[data-title-field]');
  const titleInput = requireElement<HTMLInputElement>(section, '#krc-room-title');
  const formError = requireElement<HTMLElement>(section, '[data-form-error]');
  const submit = requireElement<HTMLButtonElement>(section, '[data-submit]');
  const live = requireElement<HTMLElement>(section, '[data-cleanup-live]');
  titleInput.value = state.title;

  function renderStructure(): void {
    for (const key of [...checked]) if (!state.rows.some((row) => row.key === key)) checked.delete(key);
    peopleList.innerHTML = state.rows.map((row) => rowMarkup(row, checked.has(row.key))).join('');
    meOptions.innerHTML = meOptionsMarkup(state);
    titleField.hidden = !isGroupRoom(state);
    undoButton.hidden = !canUndo(state);
    updateMergeButton();
    updateValidation();
  }

  function updateMergeButton(): void {
    mergeButton.disabled = checked.size < 2;
    mergeButton.textContent = checked.size >= 2 ? `선택한 ${checked.size}명 합치기` : '선택한 사람 합치기';
  }

  function updateValidation(): void {
    const validation = validateCleanup(state);
    section.querySelectorAll<HTMLElement>('[data-name-error]').forEach((element) => {
      const message = validation.rowErrors.get(element.dataset.nameError ?? '') ?? '';
      element.textContent = message;
      const input = section.querySelector<HTMLInputElement>(`[data-name-input="${CSS.escape(element.dataset.nameError ?? '')}"]`);
      if (input) input.setAttribute('aria-invalid', String(Boolean(message)));
    });
    const message = validation.formError ?? (validation.rowErrors.size ? '안내가 있는 이름을 고쳐 주세요.' : '');
    if (formError.textContent !== message) formError.textContent = message;
    submit.disabled = !validation.canSubmit;
  }

  function updateNames(key: string): void {
    const row = state.rows.find((item) => item.key === key);
    if (!row) return;
    const name = displayName(row);
    const item = peopleList.querySelector<HTMLElement>(`[data-key="${CSS.escape(key)}"]`);
    const checkLabel = item?.querySelector<HTMLElement>('[data-check-label]');
    if (checkLabel) checkLabel.textContent = `${name} 합치기 선택`;
    const meLabel = meOptions.querySelector<HTMLElement>(`[data-me-label="${CSS.escape(key)}"]`);
    if (meLabel) meLabel.textContent = name;
  }

  section.addEventListener('input', (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.dataset.nameInput) {
      state = renameRow(state, target.dataset.nameInput, target.value);
      updateNames(target.dataset.nameInput);
      updateValidation();
    } else if (target === titleInput) {
      state = setTitle(state, titleInput.value);
    }
  });

  section.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.hasAttribute('data-merge-check')) {
      if (target.checked) checked.add(target.value);
      else checked.delete(target.value);
      updateMergeButton();
    } else if (target instanceof HTMLInputElement && target.name === 'krc-me') {
      state = setMe(state, target.value || null);
    } else if (target instanceof HTMLSelectElement && target.id === 'krc-me-select') {
      state = setMe(state, target.value || null);
    }
  });

  mergeButton.addEventListener('click', () => {
    const keys = [...checked];
    if (keys.length < 2) return;
    const names = state.rows.filter((row) => checked.has(row.key)).map(displayName);
    state = mergeRows(state, keys);
    checked.clear();
    renderStructure();
    const merged = state.rows.find((row) => keys.some((key) => row.members.includes(Number(key.slice(1)))));
    live.textContent = `${josa(names.join(', '), '을/를')} 한 사람으로 합쳤어요. 되돌리기로 취소할 수 있어요.`;
    if (merged) section.querySelector<HTMLInputElement>(`[data-name-input="${CSS.escape(merged.key)}"]`)?.focus();
  });

  undoButton.addEventListener('click', () => {
    state = undoMerge(state);
    renderStructure();
    live.textContent = '마지막 합치기를 되돌렸어요.';
    (canUndo(state) ? undoButton : section.querySelector<HTMLInputElement>('[data-merge-check]'))?.focus();
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const validation = validateCleanup(state);
    if (!validation.canSubmit) {
      updateValidation();
      const firstInvalid = [...validation.rowErrors.keys()][0];
      if (firstInvalid) section.querySelector<HTMLInputElement>(`[data-name-input="${CSS.escape(firstInvalid)}"]`)?.focus();
      return;
    }
    options.onSubmit(toAnalysisSetup(state), state);
  });

  requireElement<HTMLButtonElement>(section, '[data-restart]').addEventListener('click', () => options.onRestart());
  renderStructure();

  return {
    heading: requireElement(section, '#krc-cleanup-title'),
    dispose() {
      section.remove();
    },
  };
}
